/**
 * A mini-game choice, drawn.
 *
 * ## IT IS ITS OWN MODULE BECAUSE IT HAS A SECOND MOUNT
 *
 * It was a private `ChoiceIcon` inside `./DuelOverlay.tsx` and it is a component now, taking a
 * `MiniGameChoice` and a class and nothing else — no `DuelView`, no connection, no wire. That is
 * `./DuelPotBar.tsx`' extraction and for exactly its reason: the instructions modal shows what
 * each mini-game's answers *look like*, and the player asked for "the actual graphic that would
 * be shown in game". A screenshot is wrong the first time a path moves and a redrawn icon is
 * wrong immediately; one renderer over the registry's own paths cannot drift from itself.
 *
 * ## THE PATHS ARE THE REGISTRY'S AND THE COLOUR IS THE CALLER'S
 *
 * `MiniGameChoiceArt` is a viewBox and a list of path commands — no colours, no sizes, no `<svg>`
 * markup — because `@chompdome/shared` is read by three processes and two of them have no DOM.
 * So every stroke here is `currentColor`: the icon inherits whatever its container is doing —
 * muted on a resting button, the primary hue on the picked one, the card's own ink on an
 * instructions page — and is legible in both themes without a palette of its own.
 *
 * A game with no artwork falls back to `glyph`, which is text and is therefore never missing.
 * That is the same fallback a client one build behind the registry gets, and it is why `art` is
 * optional in the first place.
 */

import type { MiniGameChoice } from '@chompdome/shared/minigames';

export interface MiniGameChoiceIconProps {
  choice: MiniGameChoice;
  /** Size and any colour the caller wants; the paths themselves paint in `currentColor`. */
  className: string;
}

export function MiniGameChoiceIcon({ choice, className }: MiniGameChoiceIconProps) {
  const art = choice.art;
  if (art === undefined) {
    return (
      <span aria-hidden className={`${className} text-3xl leading-none`}>
        {choice.glyph}
      </span>
    );
  }

  return (
    <svg aria-hidden focusable="false" viewBox={art.viewBox} className={className} fill="none">
      {art.paths.map((d) => (
        <path
          key={d}
          d={d}
          stroke="currentColor"
          strokeWidth={art.strokeWidth ?? 2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}
